import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Select } from 'antd';
import {
	DndContext,
	PointerSensor,
	pointerWithin,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';

import { CHECK, CLOSE, DRAG_HANDLE } from '../icons';
import { getDataTypeMeta } from '../../utils/dictionary';
import { formatCount, joinMeta } from '../../utils/counts';
import { CURRENCY_LIST, allowedCodes, getCurrency } from '../../utils/currency';
import { unitOptionsFor } from '../../utils/measurements';
import { choicesOf } from '../../utils/changeCard/compat';
import ChoiceMapEditor from './ChoiceMapEditor';
import Composer from './Composer';
import { BUILD_VALUE, HIDE_VALUE } from './composerCopy';
import {
	dropVerdict,
	elementLabel,
	entryReads,
	entryText,
	isBlankCompose,
	isCompose,
	plainSourceName,
	targetStatus,
} from './changeCardModel';

const SOURCE_PREFIX = 'source:';
const TARGET_PREFIX = 'target:';

const STATUS_WORDS = {
	filled: 'Filled',
	default: 'Default',
	empty: 'Empty',
	required: 'Required, not filled',
	problem: 'Needs a change',
};

const typeWords = el => getDataTypeMeta(el?.dataType || 'text-small')?.label || 'Short text';

const KEYBOARD_INSTRUCTIONS = 'To fill a target element, press space or enter, move between the target elements with the arrow keys, then press space or enter again to drop, or escape to cancel.';

/**
 * The keyboard path of a drag (§ Drag to reorder's grip grammar), by index:
 * Space or Enter on a pill's grip picks it up, the arrows step through the
 * target elements top to bottom, Space or Enter drops it on the one it is
 * over, Escape (or leaving the grip) puts it back. It is the board's own, not
 * dnd-kit's keyboard sensor: that one moves the pill by geometry, and when a
 * step needs the page to scroll it can spend the arrow on the scroll, so the
 * pill lands a row short of where the arrows said.
 *
 * `keyStep(held, key, count)` → the next state: `{ index }` (−1 is still on
 * the source), or 'drop' / 'cancel', or null for a key it does not handle.
 */
const STEPS = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
export function keyStep(index, key, count) {
	if (key === 'Escape') return 'cancel';
	if (key === ' ' || key === 'Spacebar' || key === 'Enter') return 'drop';
	const step = STEPS[key];
	if (!step || !count) return null;
	if (index === -1) return { index: step > 0 ? 0 : count - 1 };
	return { index: Math.min(count - 1, Math.max(0, index + step)) };
}

function SourcePill({ source, holding, mapped, draggable, keyHeld, onGripKeyDown, onGripBlur }) {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging, transform } = useDraggable({
		id: SOURCE_PREFIX + source.name,
		disabled: !draggable,
	});
	const label = elementLabel(source);
	const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
	const lifted = isDragging || keyHeld;
	return (
		<li
			ref={setNodeRef}
			style={style}
			className={`change-card__pill${lifted ? ' is-dragging' : ''}${mapped ? ' is-mapped' : ''}`}
			data-source={source.name}
		>
			{draggable ? (
				<button
					type="button"
					ref={setActivatorNodeRef}
					className="change-card__grip"
					aria-label={`Move ${label}`}
					{...attributes}
					aria-describedby="change-card-keyboard"
					aria-pressed={keyHeld ? 'true' : 'false'}
					{...listeners}
					onKeyDown={event => onGripKeyDown(event, source.name)}
					onBlur={onGripBlur}
				>
					<DRAG_HANDLE aria-hidden="true" />
				</button>
			) : null}
			<span className="change-card__pill-text">
				<span className="change-card__pill-label">{label}</span>
				<span className="change-card__pill-meta">
					{joinMeta(typeWords(source), holding ? `held by ${formatCount(holding, 'item')}` : null)}
				</span>
			</span>
			{mapped ? <span className="change-card__mapped" title="Mapped"><CHECK aria-label="Mapped" /></span> : null}
		</li>
	);
}

function NeedsControls({ target, source, entry, answer, onUpdate }) {
	if (!answer?.needs?.length) return null;
	return answer.needs.map(need => {
		if (need.kind === 'unit') {
			return (
				<label key="unit" className="change-card__need">
					<span>{need.of === 'source' ? `${elementLabel(source)} is in` : 'Numbers are in'}</span>
					<Select
						size="small"
						showSearch
						optionFilterProp="label"
						value={entry.unit}
						options={unitOptionsFor(need.measure)}
						onChange={unit => onUpdate({ unit })}
						aria-label={`Unit for ${elementLabel(target)}`}
						popupMatchSelectWidth={false}
					/>
				</label>
			);
		}
		if (need.kind === 'currency') {
			const allowed = allowedCodes((need.of === 'source' ? source : target)?.options || {});
			const list = allowed.length ? allowed.map(getCurrency).filter(Boolean) : CURRENCY_LIST.filter(c => !c.fund);
			return (
				<label key="currency" className="change-card__need">
					<span>{need.of === 'source' ? 'Keep amounts in' : 'Numbers are in'}</span>
					<Select
						size="small"
						showSearch
						optionFilterProp="label"
						value={entry.currency}
						options={list.map(c => ({ value: c.code, label: `${c.code} · ${c.name}` }))}
						onChange={currency => onUpdate({ currency })}
						aria-label={`Currency for ${elementLabel(target)}`}
						popupMatchSelectWidth={false}
					/>
				</label>
			);
		}
		return null;
	});
}

function TargetRow({
	target,
	entry,
	source,
	answer,
	sources,
	ctx,
	dry,
	problems,
	suggestion,
	refusal,
	held,
	keyOver,
	sourcesByName,
	getElementsForCard,
	composeProblem,
	preview,
	stale,
	onAssign,
	onClear,
	onUpdate,
	onBuild,
	onCompose,
	onUsePicker,
	onAcceptSuggestion,
}) {
	const { setNodeRef, isOver: pointerOver } = useDroppable({ id: TARGET_PREFIX + target.name });
	const isOver = pointerOver || keyOver;
	const [choicesOpen, setChoicesOpen] = useState(false);
	const label = elementLabel(target);
	// "Required" from the last dry run no longer holds once a source fills the
	// row; the next answer says whether anything else does.
	// A composer with nothing in it yet fills nothing.
	const composing = isCompose(entry);
	const active = entry && !isBlankCompose(entry) ? entry : null;
	const shown = (problems || []).filter(problem => !(active && problem?.code === 'required'));
	const withLocal = composeProblem ? [...shown, { code: 'composer' }] : shown;
	const status = targetStatus(target, active, dry, withLocal.length ? withLocal : null);
	const reducible = composing && (isBlankCompose(entry) || !!plainSourceName(entry, Object.keys(sourcesByName)));
	// While a pill is held, every row says at once whether it would take it.
	const heldVerdict = held ? dropVerdict(held, target, ctx) : null;

	const options = useMemo(() => sources.map(candidate => {
		const verdict = dropVerdict(candidate, target, ctx);
		const name = elementLabel(candidate);
		return {
			value: candidate.name,
			disabled: !verdict.ok,
			searchText: name,
			label: verdict.ok ? name : (
				<span className="change-card__option-refused">
					<span>{name}</span>
					<span className="change-card__option-reason">{verdict.reason}</span>
				</span>
			),
		};
	}).sort((a, b) => Number(a.disabled) - Number(b.disabled)), [sources, target, ctx]);

	const counts = dry ? joinMeta(
		formatCount(dry.receiving, 'item') ? `${formatCount(dry.receiving, 'item')} filled` : null,
		dry.empty ? `${dry.empty} left empty` : null,
		dry.failed ? `${dry.failed} will not convert` : null,
		dry.uniqueConflicts ? `${dry.uniqueConflicts} already used elsewhere` : null,
	) : null;
	const failures = (dry?.failures || []).slice(0, 3);
	const isChoice = answer?.needs?.some(need => need.kind === 'choices');
	const unmappedChoices = isChoice && entry && source
		? choicesOf(source).filter(c => !Object.prototype.hasOwnProperty.call(entry.choices || {}, c.value)).length
		: 0;

	const classes = ['change-card__target'];
	if (isOver) classes.push('is-over');
	if (heldVerdict) classes.push(heldVerdict.ok ? 'is-accepting' : 'is-refusing');

	return (
		<li ref={setNodeRef} className={classes.join(' ')} data-target={target.name}>
			<div className="change-card__target-head">
				<div className="change-card__target-name">
					<span className="change-card__target-label">{label}</span>
					<span className="change-card__target-type">{typeWords(target)}</span>
				</div>
				<span className={`change-card__status change-card__status--${status}`}>{STATUS_WORDS[status]}</span>
			</div>
			<div className="change-card__target-fill">
				{composing ? null : (
					<Select
						className="change-card__choose"
						placeholder="Choose an element"
						showSearch
						optionFilterProp="searchText"
						value={entry?.source}
						options={options}
						onChange={onAssign}
						aria-label={`Fill ${label} from`}
						popupMatchSelectWidth={false}
					/>
				)}
				{composing ? (
					reducible ? (
						<Button size="small" type="link" className="change-card__build" onClick={onUsePicker}>{HIDE_VALUE}</Button>
					) : null
				) : (
					<Button size="small" type="link" className="change-card__build" onClick={onBuild} aria-label={`${BUILD_VALUE} for ${label}`}>{BUILD_VALUE}</Button>
				)}
				{entry ? (
					<Button
						size="small"
						type="text"
						className="change-card__clear"
						icon={<CLOSE aria-hidden="true" />}
						aria-label={`Clear ${label}`}
						onClick={onClear}
					/>
				) : null}
				{!entry && suggestion ? (
					<span className="change-card__suggestion">
						Suggested: {elementLabel(suggestion)}
						<Button size="small" type="link" onClick={onAcceptSuggestion}>Accept</Button>
					</span>
				) : null}
			</div>
			{composing ? (
				<Composer
					target={target}
					sources={Object.values(sourcesByName)}
					sourcesByName={sourcesByName}
					getElementsForCard={getElementsForCard}
					text={entryText(entry)}
					onChange={onCompose}
					problem={composeProblem}
					preview={preview}
					stale={stale}
				/>
			) : null}
			{entry && source ? <NeedsControls target={target} source={source} entry={entry} answer={answer} onUpdate={onUpdate} /> : null}
			{entry && source && isChoice ? (
				<div className="change-card__choices">
					<Button size="small" type="link" onClick={() => setChoicesOpen(open => !open)} aria-expanded={choicesOpen}>
						{choicesOpen ? 'Hide choices' : 'Map choices'}
					</Button>
					{!choicesOpen && unmappedChoices ? (
						<span className="change-card__note">{unmappedChoices === 1 ? '1 choice is not mapped yet' : `${unmappedChoices} choices are not mapped yet`}</span>
					) : null}
					{choicesOpen ? (
						<ChoiceMapEditor
							source={source}
							target={target}
							choices={entry.choices || {}}
							onChange={choices => onUpdate({ choices })}
						/>
					) : null}
				</div>
			) : null}
			{counts ? <div className="change-card__counts">{counts}</div> : null}
			{/* Only the row under the held pill says why: a reason on every
			    refusing row would push the rows below it out from under the
			    pointer mid-drag. The others are dimmed. */}
			{heldVerdict && !heldVerdict.ok && isOver ? <div className="change-card__reason" role="note">{heldVerdict.reason}</div> : null}
			{refusal && !held ? <div className="change-card__reason" role="alert">{refusal}</div> : null}
			{shown.map((problem, i) => (
				<div key={`p${i}`} className="change-card__reason" role="alert">{problem.message}</div>
			))}
			{failures.length ? (
				<ul className="change-card__failures">
					{failures.map(failure => (
						<li key={failure.code}>{failure.count > 1 ? `${failure.count} items: ` : ''}{failure.message}</li>
					))}
				</ul>
			) : null}
		</li>
	);
}

/**
 * The two panels of the utility: the source card's elements as pills, the
 * target card's elements as rows a pill is dropped on (KPR-707; ruled with
 * the owner 2026-10-21: drag, plus a Choose picker on every row that is the
 * keyboard and phone path). Whether a pill may land is the compatibility
 * table's; a refused drop changes nothing and says why on the row.
 */
export default function MappingBoard({
	sources,
	targets,
	withheld,
	mapping,
	answers,
	ctx,
	dryRun,
	problems,
	suggestions,
	isPhone,
	getElementsForCard,
	composeProblems,
	stale,
	onAssign,
	onClear,
	onUpdate,
	onBuild,
	onCompose,
	onUsePicker,
	onAcceptSuggestion,
}) {
	const [heldName, setHeldName] = useState(null);
	const [keyHeld, setKeyHeld] = useState(null); // { source, index } while a grip holds a pill
	const [refusal, setRefusal] = useState(null);
	const [announcement, setAnnouncement] = useState('');
	const boardRef = useRef(null);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
	const sourcesByName = useMemo(() => Object.fromEntries(sources.map(el => [el.name, el])), [sources]);
	const targetsByName = useMemo(() => Object.fromEntries(targets.map(el => [el.name, el])), [targets]);
	const dryTargets = useMemo(() => Object.fromEntries((dryRun?.targets || []).map(t => [t.element, t])), [dryRun]);
	const drySources = useMemo(() => Object.fromEntries((dryRun?.sources || []).map(s => [s.element, s])), [dryRun]);
	const suggestionFor = useMemo(() => Object.fromEntries((suggestions || []).map(s => [s.target, sourcesByName[s.source]])), [suggestions, sourcesByName]);
	const mappedSources = useMemo(() => {
		const names = Object.keys(sourcesByName);
		const out = new Set();
		Object.values(mapping).forEach(entry => entryReads(entry, names).forEach(name => out.add(name)));
		return out;
	}, [mapping, sourcesByName]);
	const held = heldName ? sourcesByName[heldName] : (keyHeld ? sourcesByName[keyHeld.source] : null);
	const keyOverName = keyHeld && keyHeld.index >= 0 ? targets[keyHeld.index]?.name : null;

	// The row the arrows moved to is brought into view.
	useEffect(() => {
		if (!keyOverName || !boardRef.current) return;
		const name = typeof window.CSS?.escape === 'function' ? window.CSS.escape(keyOverName) : keyOverName;
		const node = boardRef.current.querySelector(`[data-target="${name}"]`);
		if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' });
	}, [keyOverName]);

	const nameOf = id => String(id).replace(SOURCE_PREFIX, '').replace(TARGET_PREFIX, '');
	const labelOfId = (id, byName) => elementLabel(byName[nameOf(id)]);

	const announcements = {
		onDragStart: ({ active }) => `Picked up ${labelOfId(active.id, sourcesByName)}. Use the arrow keys to move between the target elements, space to drop it, escape to cancel.`,
		onDragOver: ({ active, over }) => {
			if (!over) return `${labelOfId(active.id, sourcesByName)} is not over a target element.`;
			const verdict = dropVerdict(sourcesByName[nameOf(active.id)], targetsByName[nameOf(over.id)], ctx);
			return `${labelOfId(active.id, sourcesByName)} is over ${labelOfId(over.id, targetsByName)}. ${verdict.ok ? 'It can go here.' : verdict.reason}`;
		},
		onDragEnd: ({ active, over }) => {
			if (!over) return `${labelOfId(active.id, sourcesByName)} was put back.`;
			const verdict = dropVerdict(sourcesByName[nameOf(active.id)], targetsByName[nameOf(over.id)], ctx);
			return verdict.ok
				? `${labelOfId(over.id, targetsByName)} is filled from ${labelOfId(active.id, sourcesByName)}.`
				: verdict.reason;
		},
		onDragCancel: ({ active }) => `${labelOfId(active.id, sourcesByName)} was put back.`,
	};

	const drop = (sourceName, targetName) => {
		const verdict = dropVerdict(sourcesByName[sourceName], targetsByName[targetName], ctx);
		if (!verdict.ok) {
			setRefusal({ target: targetName, reason: verdict.reason });
			return verdict;
		}
		setRefusal(null);
		// A drop on a text that already has a fill adds to it (KPR-708).
		onAssign(targetName, sourceName, { extend: true });
		return verdict;
	};

	const onGripKeyDown = (event, sourceName) => {
		if (!keyHeld || keyHeld.source !== sourceName) {
			if (event.key !== ' ' && event.key !== 'Spacebar' && event.key !== 'Enter') return;
			event.preventDefault();
			setRefusal(null);
			setKeyHeld({ source: sourceName, index: -1 });
			setAnnouncement(`Picked up ${elementLabel(sourcesByName[sourceName])}. Use the arrow keys to move between the target elements, space to drop it, escape to cancel.`);
			return;
		}
		const next = keyStep(keyHeld.index, event.key, targets.length);
		if (!next) return;
		event.preventDefault();
		const sourceLabel = elementLabel(sourcesByName[sourceName]);
		if (next === 'cancel' || (next === 'drop' && keyHeld.index < 0)) {
			setKeyHeld(null);
			setAnnouncement(`${sourceLabel} was put back.`);
			return;
		}
		if (next === 'drop') {
			const target = targets[keyHeld.index];
			setKeyHeld(null);
			const verdict = drop(sourceName, target.name);
			setAnnouncement(verdict.ok ? `${elementLabel(target)} is filled from ${sourceLabel}.` : verdict.reason);
			return;
		}
		const target = targets[next.index];
		const verdict = dropVerdict(sourcesByName[sourceName], target, ctx);
		setKeyHeld({ source: sourceName, index: next.index });
		setAnnouncement(`${sourceLabel} is over ${elementLabel(target)}. ${verdict.ok ? 'It can go here.' : verdict.reason}`);
	};

	const onGripBlur = () => {
		if (!keyHeld) return;
		setAnnouncement(`${elementLabel(sourcesByName[keyHeld.source])} was put back.`);
		setKeyHeld(null);
	};

	const handleDragEnd = ({ active, over }) => {
		setHeldName(null);
		if (!over) return;
		drop(nameOf(active.id), nameOf(over.id));
	};

	const sourceList = (
		<ul className="change-card__pills" aria-label="Source elements">
			{sources.map(source => (
				<SourcePill
					key={source.name}
					source={source}
					holding={drySources[source.name]?.holding || 0}
					mapped={mappedSources.has(source.name)}
					draggable={!isPhone}
					keyHeld={keyHeld?.source === source.name}
					onGripKeyDown={onGripKeyDown}
					onGripBlur={onGripBlur}
				/>
			))}
		</ul>
	);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={pointerWithin}
			accessibility={{ announcements }}
			onDragStart={({ active }) => { setKeyHeld(null); setRefusal(null); setHeldName(nameOf(active.id)); }}
			onDragCancel={() => setHeldName(null)}
			onDragEnd={handleDragEnd}
		>
			<p id="change-card-keyboard" className="change-card__live">{KEYBOARD_INSTRUCTIONS}</p>
			<div className="change-card__live" aria-live="assertive" aria-atomic="true">{announcement}</div>
			<div className="change-card__board" ref={boardRef}>
				<section className="change-card__panel change-card__panel--from" aria-label="From">
					{isPhone ? (
						<Collapse
							ghost
							items={[{
								key: 'from',
								label: `From: ${mappedSources.size} of ${sources.length} mapped`,
								children: sourceList,
							}]}
						/>
					) : (
						<>
							<h2 className="change-card__panel-title">From</h2>
							{sourceList}
						</>
					)}
				</section>
				<section className="change-card__panel change-card__panel--to" aria-label="To">
					{!isPhone ? <h2 className="change-card__panel-title">To</h2> : null}
					<ul className="change-card__targets">
						{targets.map(target => {
							const entry = mapping[target.name];
							const plain = entry ? plainSourceName(entry, Object.keys(sourcesByName)) : null;
							const source = plain ? sourcesByName[plain] : null;
							return (
								<TargetRow
									key={target.name}
									target={target}
									entry={entry}
									source={source}
									answer={entry ? answers[target.name] : null}
									sources={sources}
									ctx={ctx}
									dry={dryTargets[target.name]}
									problems={problems?.[target.name]}
									suggestion={suggestionFor[target.name]}
									refusal={refusal?.target === target.name ? refusal.reason : null}
									held={held}
									keyOver={keyOverName === target.name}
									sourcesByName={sourcesByName}
									getElementsForCard={getElementsForCard}
									composeProblem={composeProblems?.[target.name] || null}
									preview={dryRun?.preview}
									stale={!!stale}
									onAssign={sourceName => onAssign(target.name, sourceName)}
									onClear={() => onClear(target.name)}
									onUpdate={patch => onUpdate(target.name, patch)}
									onBuild={() => onBuild(target.name)}
									onCompose={text => onCompose(target.name, text)}
									onUsePicker={() => onUsePicker(target.name)}
									onAcceptSuggestion={() => onAcceptSuggestion(target.name)}
								/>
							);
						})}
					</ul>
					{withheld?.length ? (
						<ul className="change-card__withheld" aria-label="Elements keepr fills or you cannot change">
							{withheld.map(row => (
								<li key={row.element}>{row.reason}</li>
							))}
						</ul>
					) : null}
				</section>
			</div>
		</DndContext>
	);
}
